export type RenderQuality = "LQ" | "MQ" | "HQ";

export interface RenderQualityCapOverrides {
  pixelRatioMax?: number;
  postProcessingScale?: number;
  shadowMapSize?: number;
}

export interface ResolveRenderQualityCapsInput {
  quality: RenderQuality;
  devicePixelRatio: number;
  overrides?: RenderQualityCapOverrides;
}

export interface RenderQualityCaps {
  pixelRatio: number;
  postProcessingScale: number;
  shadowMapSize: number;
}

const ABSOLUTE_POST_PROCESSING_SCALE_MAX = 1.5;
const DEFAULT_RENDER_QUALITY: RenderQuality = "MQ";

const DEFAULT_CAPS: Record<RenderQuality, RenderQualityCaps> = {
  LQ: {
    pixelRatio: 1,
    postProcessingScale: 0.6,
    shadowMapSize: 0,
  },
  MQ: {
    pixelRatio: 1.25,
    postProcessingScale: 1,
    shadowMapSize: 1024,
  },
  HQ: {
    pixelRatio: 1.5,
    postProcessingScale: 1.25,
    shadowMapSize: 2048,
  },
};

const isSafePositiveNumber = (value: unknown): value is number => {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
};

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

export const normalizeRenderQuality = (quality: unknown): RenderQuality => {
  return quality === "LQ" || quality === "MQ" || quality === "HQ"
    ? quality
    : DEFAULT_RENDER_QUALITY;
};

export const getBrowserDevicePixelRatio = () => {
  if (typeof window === "undefined") return 1;
  return isSafePositiveNumber(window.devicePixelRatio) ? window.devicePixelRatio : 1;
};

export const resolveRenderQualityCaps = ({
  quality,
  devicePixelRatio,
  overrides,
}: ResolveRenderQualityCapsInput): RenderQualityCaps => {
  const defaults = DEFAULT_CAPS[normalizeRenderQuality(quality)];
  const safeDevicePixelRatio = isSafePositiveNumber(devicePixelRatio) ? devicePixelRatio : 1;
  const pixelRatioMax = isSafePositiveNumber(overrides?.pixelRatioMax)
    ? Math.min(overrides.pixelRatioMax, defaults.pixelRatio)
    : defaults.pixelRatio;
  const postProcessingScale = isSafePositiveNumber(overrides?.postProcessingScale)
    ? clamp(overrides.postProcessingScale, defaults.postProcessingScale, ABSOLUTE_POST_PROCESSING_SCALE_MAX)
    : defaults.postProcessingScale;
  const shadowMapSize = isSafePositiveNumber(overrides?.shadowMapSize)
    ? Math.min(overrides.shadowMapSize, defaults.shadowMapSize)
    : defaults.shadowMapSize;

  return {
    pixelRatio: Math.min(safeDevicePixelRatio, pixelRatioMax),
    postProcessingScale,
    shadowMapSize,
  };
};

export const resolveRenderQualityCapsForQuality = (
  quality: unknown,
  devicePixelRatio: number = getBrowserDevicePixelRatio(),
) => resolveRenderQualityCaps({
  quality: normalizeRenderQuality(quality),
  devicePixelRatio,
});

export const clampShadowMapSize = (mapSize: unknown, quality: unknown) => {
  const caps = resolveRenderQualityCapsForQuality(quality);
  if (caps.shadowMapSize <= 0) return 0;
  const requestedMapSize = isSafePositiveNumber(mapSize) ? Math.floor(mapSize) : caps.shadowMapSize;
  return clamp(requestedMapSize, 1, caps.shadowMapSize);
};
